import React, { useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUsersService,
  CreateUserInput,
} from '../../services/createUsers.service';
import { getErrorMessage } from '../../utils/helpers/getErrorMessage';
import EyeOpenIcon from '../../assets/icons/EyeIcon';
import EyeClosedIcon from '../../assets/icons/EyeClosedIcon';
import { getAllUsersService, User } from '../../services/getAllUsers.service';
import { authService } from '../../services/auth.service';
import {
  getAllEnvironmentsService,
  Environment,
} from '../../services/getAllEnvironment.service';
import { getAllRoles, Role } from '../../services/roles.service';
import { RoleConfig } from './roleConfig';

interface RoleCreatePageProps {
  config: RoleConfig;
  styles: Record<string, string>;
  Icon: React.ComponentType<{ width?: number; height?: number; color?: string }>;
}

const RoleCreatePage: React.FC<RoleCreatePageProps> = ({ config, styles, Icon }) => {
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    password: '',
    phone: '',
    parentId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [parents, setParents] = useState<User[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number | ''>('');
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const navigate = useNavigate();

  const currentUser = authService.getStoredUser();
  const userRole = currentUser?.role?.name;
  const userId = currentUser?.id;

  const canPickParent = !!userRole && (config.parentSelectorRoles as string[]).includes(userRole);

  useLayoutEffect(() => {
    (async () => {
      const roles = await getAllRoles();
      setAllRoles(roles);
    })();
  }, []);

  React.useEffect(() => {
    getAllEnvironmentsService
      .getAllEnvironments()
      .then((envs) => {
        setEnvironments(envs);
        if (envs.length > 0) setSelectedEnvironmentId(envs[0].id);
      })
      .catch((err) => console.error('Error fetching environments:', err));

    const fetchParents = async () => {
      try {
        if (canPickParent) {
          const roleId = allRoles.find((r) => r.name === config.parentDropdownRoleName)?.id;
          const limit = config.parentDropdownRoleName === 'ADMIN' ? 50 : 100;
          const res = await getAllUsersService.getAllUsers(roleId, limit);
          setParents(res.users);
        }
      } catch (err) {
        // ignored
      }
    };
    fetchParents();
  }, [userRole, allRoles]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedEnvironmentId(value ? Number(value) : '');
  };

  const resolveParentId = (): number | undefined => {
    if (canPickParent) {
      return formData.parentId ? Number(formData.parentId) : undefined;
    }
    if (config.autoAssignParentForRole && userRole === config.autoAssignParentForRole) {
      return userId;
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const hostname = window.location.hostname;
      const backendUrl = hostname.replace(/^admin\./, '');
      let host: string;
      if (userRole === 'ADMIN') {
        const selectedEnvironment = environments.find((env) => env.id === selectedEnvironmentId);
        host = selectedEnvironment?.host || 'flow888.app';
      } else {
        host = backendUrl || 'flow888.app';
      }

      const userData: CreateUserInput = {
        username: formData.username,
        name: formData.name,
        password: formData.password,
        role: config.graphqlRoleName,
        phone: formData.phone || undefined,
        host,
        parentId: resolveParentId(),
      };

      await createUsersService.createUser(userData);
      navigate(`${config.routeBase}/success`, {
        state: {
          username: formData.username,
          name: formData.name,
          password: formData.password,
          phoneNumber: formData.phone,
          backendUrl,
        },
      });
      return;
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconWrapper}>
        <span className={styles.userIcon}>
          <Icon width={60} height={60} color="#313485" />
        </span>
      </div>
      <h2 className={styles.title}>New {config.label}</h2>

      {error && <div className={styles.errorMessage}>{error}</div>}
      {success && <div className={styles.successMessage}>{success}</div>}

      <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <span className={styles.required}>*</span> Username
          </label>
          <div className={styles.inputWrapper}>
            <span className={styles.inputIcon}>👤</span>
            <input
              autoComplete="off"
              className={styles.input}
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder=""
              required
            />
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <span className={styles.required}>*</span> Name
          </label>
          <div className={styles.inputWrapper}>
            <span className={styles.inputIcon}>👤</span>
            <input
              autoComplete="off"
              className={styles.input}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder=""
              required
            />
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <span className={styles.required}>*</span> Password
          </label>
          <div className={styles.inputWrapper} style={{ position: 'relative' }}>
            <span className={styles.inputIcon}>🔒</span>
            <input
              autoComplete="off"
              className={styles.input}
              type="text"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder=""
              required
              style={{
                WebkitTextSecurity: showPassword ? 'none' : 'disc',
              } as React.CSSProperties}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{
                background: 'none',
                border: 'none',
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {showPassword ? (
                <EyeOpenIcon style={{ opacity: 0.7 }} />
              ) : (
                <EyeClosedIcon style={{ opacity: 1 }} />
              )}
            </button>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Phone Number</label>
          <div className={styles.inputWrapper}>
            <span className={styles.inputIcon}>📞</span>
            <input
              className={styles.input}
              type="number"
              autoComplete="off"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder=""
            />
          </div>
        </div>
        {userRole === 'ADMIN' && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.required}>*</span> Environment
            </label>
            <select
              className={styles.select}
              name="environment"
              value={selectedEnvironmentId}
              onChange={handleEnvironmentChange}
              required
            >
              <option value="">Select Environment</option>
              {environments.map((environment) => (
                <option key={environment.id} value={environment.id}>
                  {environment.name} ({environment.host})
                </option>
              ))}
            </select>
          </div>
        )}
        {canPickParent && (
          <div className={styles.formGroup}>
            <label className={styles.label}>{config.parentDropdownLabel}</label>
            <select
              className={styles.select}
              name="parentId"
              value={formData.parentId}
              onChange={handleInputChange}
              required={config.parentDropdownRoleName !== 'ADMIN'}
            >
              <option value="">Select {config.parentDropdownLabel.replace(/^Assign to /, '')}</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.username}
                </option>
              ))}
            </select>
          </div>
        )}
        <button className={styles.createBtn} type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create'}
        </button>
      </form>
    </div>
  );
};

export default RoleCreatePage;
